"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { authApi, customerApi, type SendOtpResponse } from "@/api/services";
import {
  ApiError,
  clearToken,
  clearRefreshToken,
  getToken,
  notifySignInCancelled,
  notifySignedIn,
  setToken,
  whenApiKeyReady,
} from "@/api/http";
import { resetAnalytics, track } from "@/lib/analytics";
import type {
  Customer,
  CustomerCreate,
} from "@/api/online-ordering-service/model";
import { getReferralCode, clearReferralCode } from "@/lib/referral-capture";

type AuthCtx = {
  token: string | null;
  user: Customer | null;
  isLoggedIn: boolean;
  /** True while bootstrapping (loading user from existing token). */
  loading: boolean;
  sendOtp: (phone: string) => Promise<SendOtpResponse>;
  /**
   * Resolves with `{ needsRegistration }` so callers (the sign-in modal) can
   * decide whether to close or advance to a name-entry step.
   */
  verifyOtp: (
    phone: string,
    code: string,
  ) => Promise<{ needsRegistration: boolean }>;
  signOut: () => void;
  /** Update the user's name and refresh the user state. */
  updateName: (name: string) => Promise<void>;
  /** Update the user's profile and refresh the user state. */
  updateProfile: (patch: { name?: string; email?: string }) => Promise<void>;
  /** Force a re-fetch of the current customer. */
  refresh: () => Promise<void>;
  /** True when the API layer requested a sign-in via the 401 recovery path. */
  signInRequired: boolean;
  cancelSignInPrompt: () => void;
  /**
   * True after a successful OTP verify when `GET /customers/me` returns 404 —
   * i.e. the phone is authenticated but no customer record exists yet. The
   * sign-in modal uses this to render a name-entry step before closing.
   */
  needsRegistration: boolean;
  /** Create the customer record for a freshly verified phone. */
  completeRegistration: (payload: CustomerCreate) => Promise<void>;
};

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<Customer | null>(null);
  const [loading, setLoading] = useState<boolean>(!!getToken());
  const [signInRequired, setSignInRequired] = useState(false);
  const [needsRegistration, setNeedsRegistration] = useState(false);

  // The API layer fires `tappya:auth-required` when a 401 cannot be recovered
  // by token refresh. Surface it as state so consumers (e.g. `App.tsx` chrome)
  // can pop a sign-in modal; calls are paused until we call `notifySignedIn()`
  // or `notifySignInCancelled()`.
  useEffect(() => {
    function onAuthRequired() {
      setSignInRequired(true);
    }
    window.addEventListener("tappya:auth-required", onAuthRequired);
    return () =>
      window.removeEventListener("tappya:auth-required", onAuthRequired);
  }, []);

  const fetchUser = useCallback(async (): Promise<{
    needsRegistration: boolean;
  }> => {
    try {
      const me = await customerApi.getMe();
      setUser(me);
      setNeedsRegistration(false);
      return { needsRegistration: false };
    } catch (err) {
      // Only clear the token on a true auth failure. Network errors, 5xx,
      // or the brief window where X-API-KEY isn't yet set should NOT log
      // the user out — that was the bug behind "have to log in after refresh".
      const status = err instanceof ApiError ? err.status : undefined;
      if (status === 401 || status === 403) {
        console.warn("[auth] getMe rejected with auth error, clearing token", err);
        clearToken();
        clearRefreshToken();
        setTokenState(null);
        setUser(null);
        setNeedsRegistration(false);
      } else if (status === 404) {
        // Phone is authenticated but no customer record exists yet — this
        // is the new-user path. Keep the token; surface needsRegistration so
        // the sign-in modal can prompt for a name and POST /customers.
        // Mirrors v1 home.page.ts where a 404 from getCurrentCustomer
        // triggers `this.action = 'name'`.
        setUser(null);
        setNeedsRegistration(true);
        return { needsRegistration: true };
      } else {
        console.warn("[auth] getMe failed transiently, keeping token", err);
      }
    }
    return { needsRegistration: false };
  }, []);

  const completeRegistration = useCallback(
    async (payload: CustomerCreate) => {
      const referralCode = getReferralCode();
      if (referralCode) {
        payload.referralCode = referralCode;
      }
      const created = await customerApi.create(payload);
      if (referralCode) clearReferralCode();
      setUser(created);
      setNeedsRegistration(false);
      track("registered", {});
      // Now that we have a real customer record, resume any paused calls.
      setSignInRequired(false);
      notifySignedIn();
    },
    [],
  );

  // On mount: if there's a stored token, hydrate the user — but wait for
  // BranchProvider to populate X-API-KEY first, otherwise /customers/me
  // returns 401 and we'd nuke a perfectly valid token on every reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const existing = getToken();
      if (!existing) {
        setLoading(false);
        return;
      }
      await whenApiKeyReady();
      if (cancelled) return;
      await fetchUser();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchUser]);

  const sendOtp = useCallback(async (phone: string) => {
    track("otp_requested", { phone_country: phone.slice(0, 3) });
    return authApi.sendOtp(phone);
  }, []);

  const verifyOtp = useCallback(
    async (phone: string, code: string) => {
      const res = await authApi.verifyOtp(phone, code);
      setToken(res.accessToken);
      setTokenState(res.accessToken);
      track("signed_in", { phone_country: phone.slice(0, 3) });
      const result = await fetchUser();
      if (!result.needsRegistration) {
        // Existing customer — resume any API calls that were paused waiting
        // for sign-in. New users wait until completeRegistration succeeds.
        setSignInRequired(false);
        notifySignedIn();
      }
      return result;
    },
    [fetchUser],
  );

  const cancelSignInPrompt = useCallback(() => {
    setSignInRequired(false);
    notifySignInCancelled();
  }, []);

  const signOut = useCallback(() => {
    track("signed_out");
    resetAnalytics();
    clearToken();
    setTokenState(null);
    setUser(null);
    setNeedsRegistration(false);
    // Best-effort backend logout (don't block UI).
    authApi.logout().catch((err) => {
      console.warn("[auth] backend logout failed", err);
    });
  }, []);

  const updateName = useCallback(async (name: string) => {
    // The user specifically requested using POST /customers to save the name.
    await customerApi.create({ name });
    await fetchUser();
  }, [fetchUser]);

  const updateProfile = useCallback(async (patch: { name?: string; email?: string }) => {
    await customerApi.updateMe(patch);
    await fetchUser();
  }, [fetchUser]);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    await fetchUser();
  }, [fetchUser]);

  const value = useMemo<AuthCtx>(
    () => ({
      token,
      user,
      isLoggedIn: !!token && !!user,
      loading,
      sendOtp,
      verifyOtp,
      signOut,
      updateName,
      updateProfile,
      refresh,
      signInRequired,
      cancelSignInPrompt,
      needsRegistration,
      completeRegistration,
    }),
    // [token, user, loading, sendOtp, verifyOtp, signOut, updateName, updateProfile, refresh, signInRequired, cancelSignInPrompt],
    [
      token,
      user,
      loading,
      sendOtp,
      verifyOtp,
      signOut,
      refresh,
      signInRequired,
      cancelSignInPrompt,
      needsRegistration,
      completeRegistration,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
